import type { ArtifactListingEntry } from '@crowdex/agent-contracts/api';
import { SIDE_PANEL_ITEMS, type SidePanelView } from '@/lib/side-panel/side-panel-items';

/** A composer @-mention target derived from an open panel tab or session. */
export type MentionTarget = {
  id: string;
  kind: 'terminal' | 'tab' | 'chat' | 'artifact' | 'persona' | 'app';
  label: string;
  detail: string;
  /** Rich value returned to an agent that resolves this reference. */
  value?: unknown;
};

function mentionValueEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
}

/** The artifact a mention target names, if it names one. */
export function mentionArtifact(target: MentionTarget): ArtifactListingEntry | null {
  if (target.kind !== 'artifact' || !target.value || typeof target.value !== 'object') return null;
  const artifact = target.value as Partial<ArtifactListingEntry>;
  return typeof artifact.id === 'string' &&
    typeof artifact.path === 'string' &&
    typeof artifact.space === 'string' &&
    typeof artifact.title === 'string' &&
    typeof artifact.typeTitle === 'string'
    ? (target.value as ArtifactListingEntry)
    : null;
}

/** The artifacts offered as references, by index id: resolving one needs no listing. */
const offeredArtifacts = new Map<string, ArtifactListingEntry>();

/** An artifact offered as a reference in this window, by its index id. */
export function offeredArtifact(id: string): ArtifactListingEntry | undefined {
  return offeredArtifacts.get(id);
}

/** The composer reference to an artifact of the core's index. */
export function artifactMentionTarget(artifact: ArtifactListingEntry): MentionTarget {
  offeredArtifacts.set(artifact.id, artifact);
  return {
    id: encodeURIComponent(artifact.id),
    kind: 'artifact',
    label: artifact.title,
    detail: `${artifact.typeTitle} · ${artifact.path}`,
    value: artifact,
  };
}

/** An installed app the composer's @ offers: what the agent reads of it. */
export type MentionApp = {
  plugin: string;
  title: string;
  description?: string | undefined;
  /** Its skills' resources, which the agent reads to use it. */
  skills: readonly string[];
};

/** The apps offered as references, by plugin name: resolving one needs no listing. */
const offeredApps = new Map<string, MentionApp>();

/** An app offered as a reference in this window, by its plugin name. */
export function offeredApp(plugin: string): MentionApp | undefined {
  return offeredApps.get(plugin);
}

/** The composer reference to an installed app. */
export function appMentionTarget(app: MentionApp): MentionTarget {
  offeredApps.set(app.plugin, app);
  return {
    id: app.plugin,
    kind: 'app',
    label: app.title,
    detail: app.description ?? 'App',
    value: { kind: 'app', ...app },
  };
}

/** Prevents equivalent derived targets from repeatedly updating the shell store. */
export function mentionTargetsEqual(
  left: readonly MentionTarget[],
  right: readonly MentionTarget[],
): boolean {
  return (
    left.length === right.length &&
    left.every((target, index) => {
      const candidate = right[index];
      return (
        candidate !== undefined &&
        target.id === candidate.id &&
        target.kind === candidate.kind &&
        target.label === candidate.label &&
        target.detail === candidate.detail &&
        mentionValueEqual(target.value, candidate.value)
      );
    })
  );
}

/** A named session contributing a mention target. */
type NamedSession = { id: string; name: string };
type MentionSessionGroup = {
  view: SidePanelView;
  sessions: readonly NamedSession[];
  kind: 'terminal' | 'tab';
  detail: string;
};
/**
 * Builds the mention targets advertised to the composer from the conversation's
 * artifacts, declared session panels and every other open panel tab. Pure so the derivation is unit-testable.
 */
export function buildMentionTargets(params: {
  artifacts?: readonly ArtifactListingEntry[];
  sessionGroups?: readonly MentionSessionGroup[];
  openViews: readonly SidePanelView[];
  /** The name of a view's tab as its tab bar shows it (an app's title, an artifact's). */
  viewLabel?: (view: SidePanelView) => string | undefined;
}): MentionTarget[] {
  const { artifacts = [], sessionGroups = [], openViews, viewLabel } = params;
  const sessionViews = new Set(sessionGroups.map((group) => group.view));
  return [
    ...artifacts.map(artifactMentionTarget),
    ...sessionGroups.flatMap((group) =>
      group.sessions.map((session) => ({
        id: session.id,
        kind: group.kind,
        label: session.name,
        detail: group.detail,
      })),
    ),
    ...openViews
      .filter((view) => view !== 'new-tab' && !sessionViews.has(view))
      .map((view) => ({
        id: view,
        kind: 'tab' as const,
        label:
          viewLabel?.(view) ?? SIDE_PANEL_ITEMS.find((item) => item.id === view)?.label ?? view,
        detail: 'Panel tab',
      })),
  ];
}
